import { Flex } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "@refinedev/core";
import styled from "styled-components";
import {
  DEFAULT_COLOR,
  SCENE_SCROLL_MARGIN_TOP,
  TEMPLATE_OUTLINE_WIDTH,
  VIDEO_FPS,
} from "../../constants";
import { TemplateCustomTexts } from "./Preview/components/TemplateCustomTexts";
import { TemplateImages } from "./Preview/components/TemplateImages";
import { TemplateVideos } from "./Preview/components/TemplateVideos";
import { ColoredBackground } from "./Preview/components/ColoredBackground";
import {
  useTemplateEditorStore,
  useTemplateEditorStoreApi,
} from "../../store/useTemplateEditorStore";
import { useTemplateData } from "../../hooks/useTemplateData";
import { useTemplatesStatusStore } from "../../store/useTemplatesStatusStore";
import { TemplatePlayer } from "./TemplatePlayer";
import { TemplateShapes } from "./Preview/components/TemplateShapes";
import { TemplateTable } from "./Preview/components/TemplateTable";
import { MoveableRegistryProvider } from "./Preview/moveableRegistry";
import { MoveableLayer } from "./Preview/components/MoveableLayer";
import { ItemControlsPopover } from "./Preview/components/ItemControlsPopover";
import { EditableLogo } from "./Preview/components/EditableLogo";
import { Presenter } from "./Preview/components/Presenter";
import { ImageVideoPreview } from "../common/ImageVideoPreview";
import { BodyView, ITEM_TYPES, TabKeys } from "../../types";
import { TemplateMainImage } from "./Preview/components/TemplateMainImage";
import { Slide } from "pages/media/types/";
import { TemplateParameteredTexts } from "./Preview/components/TemplateParameteredTexts";

type SlideEditableTemplateProps = {
  previewWidth: number;
  previewHeight: number;
  slide: Slide;
};

export const SlideEditableTemplate = ({
  previewWidth,
  previewHeight,
  slide,
}: SlideEditableTemplateProps) => {
  const {
    mediaGenerationSettingsData: { mediaAspectRatio, mediaGenerationSettings },
  } = useTemplateData();

  const {
    templateState,
    updateTemplateState,
    setCurrentItem,
    setTransition,
    slideNameToTemplateIdMap,
  } = useTemplateEditorStore((store) => ({
    templateState: store.templateState,
    updateTemplateState: store.updateTemplateState,
    setCurrentItem: store.setCurrentItem,
    setTransition: store.setTransition,
    slideNameToTemplateIdMap: store.slideNameToTemplateIdMap,
  }));

  const isTransitionSlide = !!mediaGenerationSettings?.chapters.some(
    (c) => c.transition_template_id === slideNameToTemplateIdMap[slide.uuid]
  );

  const {
    presenter,
    images,
    videos,
    parameteredTexts,
    figure,
    customTexts,
    slideLayoutShapes,
    shapes,
    background,
    currentItem,
    mainImage,
  } = templateState;

  const { addTemplateUpdate, setActiveTab } = useTemplatesStatusStore(
    (state) => ({
      addTemplateUpdate: state.addTemplateUpdate,
      setActiveTab: state.setActiveTab,
    })
  );

  const templateRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const isInitialScrollRunRef = useRef(true);
  const templateEditorStoreApi = useTemplateEditorStoreApi();

  /**
   * Scrolls the template into view on aspect-ratio changes, and on the initial
   * mount only when the selection came from a thumbnail click. Scene clicks
   * and scroll-stop activations leave the user's scroll position alone.
   */
  useEffect(() => {
    if (isInitialScrollRunRef.current) {
      isInitialScrollRunRef.current = false;
      const source = templateEditorStoreApi.getState().selectionSource;
      if (source === "scene") return;
    }
    const templateElement = templateRef.current;
    if (templateElement) {
      templateElement.style.setProperty(
        "scroll-margin-block-start",
        `${SCENE_SCROLL_MARGIN_TOP}px`
      );
      // Use double rAF to ensure layout has settled before scrolling
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          templateElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      });
    }
  }, [mediaAspectRatio, templateEditorStoreApi]);

  const t = useTranslate();

  const backgroundItem:
    | {
        view: BodyView;
        assetId?: string;
        assetPath?: string;
      }
    | undefined = useMemo(() => {
    if (background.bodyView === "Video") {
      return {
        view: "Video",
        assetId: background.bodyVideoAsset,
        assetPath: background.bodyVideo,
      };
    } else if (background.bodyView === "Picture") {
      return {
        view: "Picture",
        assetId: background.bodyImageAsset,
        assetPath: background.bodyImage,
      };
    } else {
      return undefined;
    }
  }, [background]);

  const video = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const handlePlayPause = async () => {
    if (isPlaying) {
      video.current?.pause();
      setIsPlaying(false);
    } else if (video.current) {
      video.current.volume = 0;
      if (currentTime < video.current.duration) await video.current.play();
      setIsPlaying(true);
    }
  };

  const handleRefTimeUpdate = (
    current?: HTMLAudioElement | HTMLVideoElement | null
  ) => {
    if (current) {
      if (Math.abs(current.currentTime - duration) < 0.1) {
        setCurrentTime(0);
        setIsPlaying(false);
      } else {
        setCurrentTime(current.currentTime);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (video.current) {
      setDuration(video.current.duration);
      // Mirrors EditableTemplate: video length is the fallback transition duration
      if (isTransitionSlide) {
        setTransition({
          assetDuration: Math.round(video.current.duration * VIDEO_FPS),
        });
      }
    }
  };

  const showDeleteButton = useMemo(() => {
    if (currentItem?.type !== ITEM_TYPES.Background) return false;
    return background.bodyView !== "Color";
  }, [currentItem, background]);

  const handleBackgroundRemove = () => {
    const updatedState = {
      background: {
        bodyView: "Color" as const,
        bodyImage: undefined,
        bodyImageAsset: undefined,
        bodyVideo: undefined,
        bodyVideoAsset: undefined,
      },
    };
    updateTemplateState(updatedState);
    addTemplateUpdate();
  };

  const showVideoControls = background.bodyView === "Video";

  return (
    <Flex
      ref={templateRef}
      vertical
      justify="center"
      align="center"
      gap={8}
      style={{
        height: "100%",
        width: "100%",
        overflow: "hidden",
        padding: TEMPLATE_OUTLINE_WIDTH,
        marginBlockEnd: showVideoControls ? 8 : 18,
      }}
    >
      <StyledWrapper
        className="template-preview"
        data-testid="template-preview"
      >
        <ItemControlsPopover
          open={showDeleteButton}
          placement="rightTop"
          arrow={false}
          onDelete={handleBackgroundRemove}
          getPopupContainer={(triggerNode) => {
            const container =
              document.getElementsByClassName("template-preview")[0]
                ?.parentElement;
            if (container) {
              return container;
            }
            return triggerNode;
          }}
          deleteButtonTooltip={t(
            "components.templates.templatePreview.removeBackground",
            {
              bodyView: t(
                `components.templates.settings.transitionView.${background.bodyView.toLowerCase()}`
              ).toLowerCase(),
            }
          )}
          canDuplicate={false}
        >
          <StyledPreview
            ref={previewRef}
            data-interactive
            width={previewWidth}
            height={previewHeight}
            $backgroundColor={DEFAULT_COLOR}
          >
            <MoveableRegistryProvider>
              <ColoredBackground
                width={previewWidth}
                height={previewHeight}
                backgroundColor={background.bodyColor}
              />
              {backgroundItem && (
                <ImageVideoPreview
                  ref={video}
                  type={
                    backgroundItem.view === "Video"
                      ? ITEM_TYPES.Video
                      : ITEM_TYPES.Image
                  }
                  assetId={backgroundItem.assetId}
                  path={backgroundItem.assetPath}
                  videoProps={{
                    onTimeUpdate: () => handleRefTimeUpdate(video.current),
                    onLoadedMetadata: handleLoadedMetadata,
                  }}
                  style={{
                    pointerEvents: "none",
                  }}
                  onClick={() => {
                    setCurrentItem({ type: ITEM_TYPES.Background });
                    setActiveTab(TabKeys.objects);
                  }}
                />
              )}
              <EditableLogo
                previewWidth={previewWidth}
                previewHeight={previewHeight}
              />
              {presenter.presenter && (
                <Presenter
                  draggable={true}
                  presenter={presenter}
                  previewWidth={previewWidth}
                  previewHeight={previewHeight}
                />
              )}
              <TemplateMainImage
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                slide={slide}
                mainImage={mainImage}
                editable
              />
              <TemplateParameteredTexts
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                texts={parameteredTexts ?? []}
                figure={figure}
                editable
              />
              <TemplateImages
                templateImages={images}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                editable
              />
              <TemplateVideos
                templateVideos={videos}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                editable
              />
              <TemplateCustomTexts
                customTexts={customTexts}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                editable
              />
              <TemplateShapes
                shapes={[...shapes, ...(slideLayoutShapes ?? [])]}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                editable
              />
              {templateState.table && "bullet_points" in slide.placeholders && (
                <TemplateTable
                  table={templateState.table}
                  bulletPoints={slide.placeholders.bullet_points}
                  previewWidth={previewWidth}
                  previewHeight={previewHeight}
                  editable
                />
              )}
              <MoveableLayer
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                rootRef={previewRef}
              />
            </MoveableRegistryProvider>
          </StyledPreview>
        </ItemControlsPopover>
      </StyledWrapper>
      {showVideoControls && (
        <TemplatePlayer
          data-interactive
          previewWidth={previewWidth}
          currentTime={currentTime}
          duration={duration}
          isPlaying={isPlaying}
          onSliderChange={(value: number) => {
            if (video.current) {
              setCurrentTime(value);
              video.current.currentTime = value;
            }
          }}
          onPlayPauseClick={handlePlayPause}
        />
      )}
    </Flex>
  );
};

export const StyledWrapper = styled.div`
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  width: fit-content;
  box-shadow: ${({ theme }) =>
    `0 0 0 ${TEMPLATE_OUTLINE_WIDTH}px ${theme.colorPrimary}`};
  & .spin-wrapper,
  & .ant-spin-container {
    height: 100%;
  }
`;

interface StyledPreviewProps {
  width: number;
  height: number;
  $backgroundColor?: string | null;
}

export const StyledPreview = styled.div<StyledPreviewProps>`
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  position: relative;
  overflow: hidden;
`;
